import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import "./Register.css";
import "./EditPosts.css";

// State and city data for USA and India
const countryData = {
  US: {
    name: "United States",
    states: {
      CA: {
        name: "California",
        cities: ["San Jose"]
      },
    }
  },
  IN: {
    name: "India",
    states: {
      KA: {
        name: "Karnataka",
        cities: ["Udupi", "Kundapura", "Karkala", "Byndoor", "Brahmavar", "Hebri", "Kaup"]
      }
    }
  }
};

const CompleteProfile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { email, from } = location.state || {};
  const API_URL = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';

  const [username, setUsername] = useState("");
  const [extraInfo, setExtraInfo] = useState({
    first_name: "",
    last_name: "",
    address: "",
    contact_number: "",
    user_country: "",
    user_state: "",
    user_city: ""
  });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [countryCode, setCountryCode] = useState("+91"); // Default to India
  
  // Country, state, city dropdown data
  const [countries] = useState([
    { code: "US", name: "United States" },
    { code: "IN", name: "India" }
  ]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  const handleExtraChange = (e) => {
    const { name, value } = e.target;
    setExtraInfo(prev => {
      const newInfo = { ...prev, [name]: value };
      
      // Reset dependent fields when country or state changes
      if (name === "user_country") {
        newInfo.user_state = "";
        newInfo.user_city = "";
      } else if (name === "user_state") {
        newInfo.user_city = "";
      }
      
      return newInfo;
    });
  };

  const handleUsernameChange = (e) => {
    setUsername(e.target.value);
  };

  const handleExtraSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(`${API_URL}/user/complete_profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          username,
          ...extraInfo,
          contact_number: countryCode + extraInfo.contact_number
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setSuccess("Profile completed! Redirecting...");
        localStorage.setItem("token", data.token);
        try {
          const decoded = jwtDecode(data.token);
          const userIdFromToken = decoded?.sub;
          if (userIdFromToken) {
            localStorage.setItem("user_id", String(userIdFromToken));
          }
        } catch (decodeErr) {
          console.error("Unable to read user id from token", decodeErr);
        }
        setTimeout(() => navigate("/onboarding", { replace: true, state: { from: from || "/home" } }), 1200);
      } else {
        setError(data.error || "Failed to save information!");
      }
    } catch (err) {
      setError("Unable to connect to the server. Please try again later.");
    } finally {
      setIsLoading(false);
    }
  };

  // Update states when country changes
  useEffect(() => {
    if (extraInfo.user_country && countryData[extraInfo.user_country]) {
      const countryStates = Object.entries(countryData[extraInfo.user_country].states).map(
        ([code, state]) => ({ code, name: state.name })
      );
      setStates(countryStates);
    } else {
      setStates([]);
      setCities([]);
    }
  }, [extraInfo.user_country]);

  // Update cities when state changes
  useEffect(() => {
    if (extraInfo.user_country && extraInfo.user_state && 
        countryData[extraInfo.user_country]?.states[extraInfo.user_state]) {
      setCities(countryData[extraInfo.user_country].states[extraInfo.user_state].cities);
    } else {
      setCities([]);
    }
  }, [extraInfo.user_state]);

  useEffect(() => {
    if (!username) return;
    const timer = setTimeout(async () => {
      try {
        const response = await fetch(`${API_URL}/user/check_username`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username }),
        });
        const data = await response.json();
        if (data.available === false) {
          setError("Username is already taken");
        } else if (error === "Username is already taken") {
          setError(""); // Clear error if username becomes available
        }
      } catch (err) {
        // Optionally handle error
      }
    }, 500); // Debounce

    return () => clearTimeout(timer);
  }, [username]);

  return (
    <div className="register-container">
      <div className="register-card">
        <div className="welcome-header">
          <h1>Complete Your Profile</h1>
          <p className="welcome-subtitle">Tell us more about yourself</p>
        </div>
        <form onSubmit={handleExtraSubmit} className="register-form">
          <div className="input-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              name="username"
              value={username}
              onChange={handleUsernameChange}
              placeholder="Choose a username"
              required
            />
          </div>
          <div className="input-group">
            <label htmlFor="first_name">First Name</label>
            <input
              type="text"
              id="first_name"
              name="first_name"
              value={extraInfo.first_name}
              onChange={handleExtraChange}
              placeholder="Your first name"
              required
            />
          </div>
          <div className="input-group">
            <label htmlFor="last_name">Last Name</label>
            <input
              type="text"
              id="last_name"
              name="last_name"
              value={extraInfo.last_name}
              onChange={handleExtraChange}
              placeholder="Your last name"
              required
            />
          </div>
          <div className="input-group">
            <label htmlFor="address">Address</label>
            <input
              type="text"
              id="address"
              name="address"
              value={extraInfo.address}
              onChange={handleExtraChange}
              placeholder="Your address"
            />
          </div>
          <div className="input-group">
            <label htmlFor="contact_number">Contact Number</label>
            <div style={{ display: "flex", alignItems: "center" }}>
              <select
                value={countryCode}
                onChange={e => setCountryCode(e.target.value)}
                style={{ marginRight: 8, padding: 4 }}
                required
              >
                <option value="+91">🇮🇳 +91 (India)</option>
                <option value="+1">🇺🇸 +1 (USA)</option>
              </select>
              <input
                type="text"
                id="contact_number"
                name="contact_number"
                value={extraInfo.contact_number}
                onChange={handleExtraChange}
                placeholder="Phone number"
                required
                maxLength={10}
                pattern="\d{10}"
              />
            </div>
          </div>
          
          <div className="input-group">
            <label htmlFor="user_country">Country</label>
            <select
              id="user_country"
              name="user_country"
              value={extraInfo.user_country}
              onChange={handleExtraChange}
              required
            >
              <option value="">Select Country</option>
              {countries.map(country => (
                <option key={country.code} value={country.code}>
                  {country.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="input-group">
            <label htmlFor="user_state">State</label>
            <select
              id="user_state"
              name="user_state"
              value={extraInfo.user_state}
              onChange={handleExtraChange}
              required
              disabled={!extraInfo.user_country}
            >
              <option value="">Select State</option>
              {states.map(state => (
                <option key={state.code} value={state.code}>
                  {state.name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="input-group">
            <label htmlFor="user_city">City</label>
            <select
              id="user_city"
              name="user_city"
              value={extraInfo.user_city}
              onChange={handleExtraChange}
              required
              disabled={!extraInfo.user_state}
            >
              <option value="">Select City</option>
              {cities.map((city, index) => (
                <option key={index} value={city}>
                  {city}
                </option>
              ))}
            </select>
          </div>
          
          {error && <p className="error-message">{error}</p>}
          {success && <p className="success-message">{success}</p>}
          <button
            type="submit"
            className="register-button"
            disabled={isLoading}
          >
            {isLoading ? (
              <span className="button-spinner"></span>
            ) : (
              "Save & Continue"
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default CompleteProfile;